import { Controller } from "@hotwired/stimulus"

// Drives the app navbar's responsive hamburger drawer.
//
// On BS5 pages (modern `app` layout) Bootstrap's own offcanvas JS opens/closes
// the drawer via the button's data-bs-toggle; this controller only *syncs* the
// hamburger's open state to the bars-to-X morph by listening to the offcanvas's
// own show/hide events (so the X also reverts on backdrop/Esc/close-button).
//
// On BS2.3 pages (legacy layouts, dual-rendered in redesign Phase B) the
// legacy.js entrypoint ships no Bootstrap, so there is no offcanvas JS at all.
// We detect that (window.bootstrap is absent) and drive a vanilla drawer
// ourselves: toggle `.show` on the target, manage a backdrop, and close on the
// drawer's close button, the backdrop, or Esc. The CSS for both lives in
// bs23_transition_skin.scss (`.app-navbar-offcanvas` / `.app-offcanvas-backdrop`).
export default class extends Controller {
  connect() {
    const selector = this.element.getAttribute("data-bs-target")
    this.offcanvas = selector && document.querySelector(selector)
    if (!this.offcanvas) return

    if (window.bootstrap?.Offcanvas) {
      // BS5 present: just mirror its show/hide events onto the hamburger.
      this.open = () => this.element.classList.add("is-open")
      this.close = () => this.element.classList.remove("is-open")
      this.offcanvas.addEventListener("show.bs.offcanvas", this.open)
      this.offcanvas.addEventListener("hide.bs.offcanvas", this.close)
      return
    }

    // BS2.3 fallback: own the whole open/close lifecycle.
    this.legacy = true
    this.toggle = this.toggle.bind(this)
    this.hide = this.hide.bind(this)
    this.onKeydown = (e) => { if (e.key === "Escape") this.hide() }

    this.element.addEventListener("click", this.toggle)
    this.dismisser = this.offcanvas.querySelector('[data-bs-dismiss="offcanvas"]')
    this.dismisser?.addEventListener("click", this.hide)
  }

  disconnect() {
    if (!this.offcanvas) return
    if (this.legacy) {
      this.element.removeEventListener("click", this.toggle)
      this.dismisser?.removeEventListener("click", this.hide)
      this.removeBackdrop()
      document.removeEventListener("keydown", this.onKeydown)
    } else {
      this.offcanvas.removeEventListener("show.bs.offcanvas", this.open)
      this.offcanvas.removeEventListener("hide.bs.offcanvas", this.close)
    }
  }

  // ---- BS2.3 vanilla drawer ----

  toggle(e) {
    e.preventDefault()
    this.offcanvas.classList.contains("show") ? this.hide() : this.show()
  }

  show() {
    this.offcanvas.classList.add("show")
    this.element.classList.add("is-open")
    this.element.setAttribute("aria-expanded", "true")

    this.backdrop = document.createElement("div")
    this.backdrop.className = "app-offcanvas-backdrop"
    this.backdrop.addEventListener("click", this.hide)
    // Append INSIDE .app-navbar (the drawer's parent), not <body>. .app-navbar
    // is position:sticky + z-index:1030, so it's its own stacking context that
    // traps the drawer's z-index:1045. A body-level backdrop at 1040 would then
    // render above the whole 1030 context — over the drawer. Inside the same
    // context, 1040 < 1045 keeps the drawer on top; the backdrop still covers
    // the viewport (position:fixed, no transformed ancestor).
    const host = this.element.closest(".app-navbar") || document.body
    host.appendChild(this.backdrop)
    document.addEventListener("keydown", this.onKeydown)
  }

  hide() {
    this.offcanvas.classList.remove("show")
    this.element.classList.remove("is-open")
    this.element.setAttribute("aria-expanded", "false")
    this.removeBackdrop()
    document.removeEventListener("keydown", this.onKeydown)
  }

  removeBackdrop() {
    this.backdrop?.remove()
    this.backdrop = null
  }
}
